/**
 * cave_gen — CAVE INTERIORS (terrain wave 2026-09-08): a Perlin-worm tunnel
 * carved as a signed-distance field and meshed by marching cubes. Pure and
 * worker-safe (runs in cave_worker); deterministic per site.
 *
 * The worm (the Minecraft/Caveworm family: a walk whose heading drifts each
 * segment, a sphere/capsule hollowed around every step, radius wandering
 * slowly) starts at the site's mouth heading UPHILL into the hill and is kept
 * under the terrain: whenever the ceiling would come within CLEARANCE of the
 * surface the walk pitches down. A round tunnel floor is awkward to walk, so
 * the cavity is intersected with a flat floor plane hung FLOOR_DROP below the
 * walk axis. Two chambers (mid-way and at the end) widen the worm.
 *
 * THE PORTAL (2026-09-10, "the cave mesh is misaligned with the terrain
 * entrance … build it to look natural"). The terrain is a 5.3 m triangle grid
 * and the tunnel is a random walk: a fixed strip cut out of the ground never
 * matched the cavity (at the reported cave 36 % of the hole looked into
 * nothing and 40 % of the open cavity lay under uncut ground), and even a
 * matched hole would have a 5 m-jagged rim. So the mouth is now a ROCK
 * OUTCROP built into the SAME field: a noisy elliptical cap of rock over the
 * ground around the mouth (`portalDome`), the tunnel carved through it — the
 * opening is an arch in the outcrop's front face, the ground's jagged hole
 * lies under the outcrop's skin, and the walls are one continuous surface
 * from the arch into the hill. The entrance is a short LEVEL hall whose floor
 * is a 20 cm doorstep over the apron, then the tunnel dives at a fixed pitch
 * so the ground closes over it inside the outcrop; the first three segments
 * keep the heading. The terrain cut (`caveCutsTriangle`, the chunk assembler)
 * follows the actual cavity — any triangle under the outcrop that touches
 * the air is dropped — so the hole can never be wider than the roof.
 *
 * Meshing: marching cubes over the plan's bounding box on a VOXEL grid using
 * three's edge/tri tables. Inside the outcrop's footprint the field is
 * min(ground-and-outcrop, cavity); outside it is the cavity alone and
 * triangles ABOVE the terrain surface are dropped (as before). Bare-ground
 * duplicates the field would emit at the outcrop's rim are dropped too.
 * Vertices carry the field gradient as a smooth normal pointing into the air.
 * Positions are relative to the mouth point so the mesh can sit at the site
 * origin.
 */
import { edgeTable, triTable } from 'three/addons/objects/MarchingCubes.js';
import { terrainHeight } from '@shared/terrain.js';
import { settlementRng } from '@shared/settlements.js';
import { type CaveSite } from '@shared/caves.js';

export const CAVE_VOXEL = 0.8;
/** Rock kept between the tunnel ceiling and the terrain surface (m). */
const CLEARANCE = 3.5;
/** The flat floor hangs this far (m) under the walk axis — a CONSTANT, not a
 *  radius fraction, so every capsule of a walk shares one continuous floor
 *  line and a widening never steps the floor. */
const FLOOR_DROP = 1.25;
const SEG_LEN = 6.5;
/** Steepest descent (rad) the walk may take (≈ 26°, under the KCC's 45° climb limit). */
const PITCH_MIN = -0.45;
/** Rock the ceiling must keep under the NATURAL surface at `along` metres into
 *  the hill: none inside the portal zone (the outcrop is the roof there), then
 *  CLEARANCE_RAMP per metre up to CLEARANCE. */
const CLEARANCE_RAMP = 0.3;
export const CAVE_PORTAL_IN = 12;
export function caveClearanceNeeded(along: number): number {
  if (along <= CAVE_PORTAL_IN) return 0;
  return Math.min(CLEARANCE, (along - CAVE_PORTAL_IN) * CLEARANCE_RAMP);
}

// ── The portal outcrop ──────────────────────────────────────────────────────
/** Ellipse (mouth-relative: u along the heading, v across) of the rock cap
 *  over the ground, and its peak height. Sized so it roofs every 5.3 m terrain
 *  triangle the cut can drop: the cavity crosses the ground plane between
 *  u ≈ 5 (the hall's floor dips under) and u ≈ 13 (the ceiling is under the
 *  natural ground on the gentlest hill), ±2.7 m across, plus one triangle. */
export const CAVE_PORTAL = { u0: 7, a: 14, b: 13, h: 7 } as const;
/** How far in front of the mouth the outcrop can reach (rim wobble included). */
export const CAVE_PORTAL_FRONT = CAVE_PORTAL.a * 1.08 - CAVE_PORTAL.u0;
/** The walk axis height at the mouth: the floor is a doorstep this much over the apron. */
export const MOUTH_FLOOR_LIFT = 0.2;
export const MOUTH_AXIS_Y = FLOOR_DROP + MOUTH_FLOOR_LIFT;
/** Entrance tunnel radius (m) — a 4.4 m arch, low enough to keep a voxel of rock over it at the outcrop's front. */
export const MOUTH_R = 2.2;
/** The walk starts this far in front of the mouth point — beyond the outcrop's front, so the hall opens on its face. */
const MOUTH_START_U = -(CAVE_PORTAL.a * 1.08 - CAVE_PORTAL.u0) - 1;
/** The level hall ends this far past the mouth point, then the dive. */
const HALL_END_U = 2;
/** Fixed descent (rad, ≈ 23°) of the two segments after the hall — the
 *  ground closes over the tunnel inside the outcrop on any hillside. */
const DIVE_PITCH = -0.4;
/** Segments that keep the mouth heading (no yaw drift): hall + two dives. */
const FORCED_SEGS = 3;
/** Terrain triangles within this signed distance of the cavity air are cut. */
export const CAVE_CUT_MARGIN = 0.15;

function hash2(ix: number, iz: number, seed: number): number {
  let h = (Math.imul(ix | 0, 0x27d4eb2d) ^ Math.imul(iz | 0, 0x165667b1) ^ (seed | 0)) | 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
/** Smooth value noise in [0, 1) — the rock's lumps and the rim's wobble. */
function vnoise(x: number, z: number, seed: number): number {
  const ix = Math.floor(x), iz = Math.floor(z);
  const fx = x - ix, fz = z - iz;
  const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
  const a = hash2(ix, iz, seed), b = hash2(ix + 1, iz, seed), c = hash2(ix, iz + 1, seed), d = hash2(ix + 1, iz + 1, seed);
  const top = a + (b - a) * sx, bot = c + (d - c) * sx;
  return top + (bot - top) * sz;
}

/**
 * Height (m) of the portal outcrop over the ground at mouth-relative (x, z);
 * 0 outside its footprint. An elliptical cap (vertical at its rim — a rock
 * face, not a mound) with the rim breathing ±12 % around the perimeter and
 * the height lumped ±18 % by noise, both seeded by the site.
 */
export function portalDome(site: CaveSite, x: number, z: number): number {
  const u = x * site.dx + z * site.dz, v = -x * site.dz + z * site.dx;
  const nu = (u - CAVE_PORTAL.u0) / CAVE_PORTAL.a, nv = v / CAVE_PORTAL.b;
  const q0 = nu * nu + nv * nv;
  if (q0 >= 1.3) return 0;
  const ang = Math.atan2(nv, nu);
  const wob = 1 + 0.08 * (vnoise(3 + 1.5 * Math.cos(ang), 3 + 1.5 * Math.sin(ang), site.caveSeed) * 2 - 1);
  const q = q0 / (wob * wob);
  if (q >= 1) return 0;
  const cap = Math.sqrt(1 - q);
  const n = vnoise(u * 0.22 + 3.1, v * 0.22 + 5.7, site.caveSeed ^ 0x5bd1e995);
  return CAVE_PORTAL.h * cap * (0.9 + 0.2 * n);
}

export interface CaveCapsule { ax: number; ay: number; az: number; bx: number; by: number; bz: number; r: number; /** a chamber sphere (no floor of its own — it takes the tunnel's) */ chamber?: boolean }
export interface CavePlan {
  site: CaveSite;
  /** Capsules in mouth-relative coordinates (x, y, z with y absolute-relative to site.h). */
  caps: CaveCapsule[];
  /** Chamber centres (mouth-relative) — crystal + light anchors. */
  chambers: Array<{ x: number; y: number; z: number; r: number }>;
  /** Path points (mouth-relative) for crystals along the walls. */
  path: Array<{ x: number; y: number; z: number; r: number }>;
  bbox: { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number };
}

/** Plan the worm for a site (needs the terrain to stay underground). */
export function planCave(seed: string, site: CaveSite): CavePlan {
  const rng = settlementRng(`${seed}:cave:${site.caveSeed}`);
  const n = 11 + Math.floor(rng() * 7);
  const yaw0 = Math.atan2(site.dz, site.dx);
  let yaw = yaw0;
  let pitch = 0;
  // The walk starts just in front of the mouth with its floor a doorstep over
  // the apron (the cavity in front of the ground is air in air — no cut).
  let px = site.dx * MOUTH_START_U, py = MOUTH_AXIS_Y, pz = site.dz * MOUTH_START_U;
  let r = MOUTH_R;
  const path: CavePlan['path'] = [{ x: px, y: py, z: pz, r }];
  const caps: CaveCapsule[] = [];
  const chambers: CavePlan['chambers'] = [];
  const midChamber = Math.floor(n * 0.55);
  for (let i = 1; i <= n; i++) {
    const forced = i <= FORCED_SEGS;
    if (forced) {
      yaw = yaw0;
      pitch = i === 1 ? 0 : DIVE_PITCH;
    } else {
      yaw += (rng() * 2 - 1) * 0.55;
      pitch += (rng() * 2 - 1) * 0.12;
      pitch = Math.max(PITCH_MIN, Math.min(0.3, pitch));
    }
    const len = i === 1 ? HALL_END_U - MOUTH_START_U : SEG_LEN;
    const nx = px + Math.cos(yaw) * Math.cos(pitch) * len;
    const nz = pz + Math.sin(yaw) * Math.cos(pitch) * len;
    let ny = py + Math.sin(pitch) * len;
    let nr = forced ? MOUTH_R + (i - 1) * 0.15 : Math.max(2.0, Math.min(3.4, r + (rng() * 2 - 1) * 0.5));
    const chamber = i === midChamber || i === n;
    if (chamber) nr = 5.5 + rng() * 2.5;
    // Stay under the hill: the ceiling keeps caveClearanceNeeded(along) of rock
    // under the NATURAL surface — nothing in the portal zone (the outcrop is
    // the roof), ramping to CLEARANCE past it.
    const along = (nx * site.dx + nz * site.dz);
    const need = caveClearanceNeeded(along);
    if (need > 0) {
      const surf = terrainHeight(seed, site.x + nx, site.z + nz) - site.h;
      const maxY = surf - need - nr;
      if (ny > maxY) { ny = maxY; pitch = Math.min(pitch, -0.05); }
    }
    // The tunnel capsule keeps the tunnel radius into a chamber.
    const useR = Math.min(r, nr);
    caps.push({ ax: px, ay: py, az: pz, bx: nx, by: ny, bz: nz, r: useR });
    if (chamber) {
      // The chamber sphere sits ON the walk axis: its flat floor is the tunnel's.
      caps.push({ ax: nx, ay: ny, az: nz, bx: nx, by: ny + 0.01, bz: nz, r: nr, chamber: true });
      chambers.push({ x: nx, y: ny, z: nz, r: nr });
    }
    px = nx; py = ny; pz = nz; r = chamber ? 2.6 : (forced ? nr : useR);
    path.push({ x: px, y: py, z: pz, r: useR });
  }
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  for (const c of caps) {
    x0 = Math.min(x0, c.ax - c.r, c.bx - c.r); x1 = Math.max(x1, c.ax + c.r, c.bx + c.r);
    y0 = Math.min(y0, c.ay - c.r, c.by - c.r); y1 = Math.max(y1, c.ay + c.r, c.by + c.r);
    z0 = Math.min(z0, c.az - c.r, c.bz - c.r); z1 = Math.max(z1, c.az + c.r, c.bz + c.r);
  }
  // The outcrop's footprint (rim wobble included) joins the box in XZ; its
  // height range is settled at mesh time from the sampled ground.
  const ea = CAVE_PORTAL.a * 1.08, eb = CAVE_PORTAL.b * 1.08;
  const ex = Math.abs(site.dx) * ea + Math.abs(site.dz) * eb, ez = Math.abs(site.dz) * ea + Math.abs(site.dx) * eb;
  const ecx = site.dx * CAVE_PORTAL.u0, ecz = site.dz * CAVE_PORTAL.u0;
  x0 = Math.min(x0, ecx - ex); x1 = Math.max(x1, ecx + ex);
  z0 = Math.min(z0, ecz - ez); z1 = Math.max(z1, ecz + ez);
  const pad = CAVE_VOXEL * 2;
  return { site, caps, chambers, path, bbox: { x0: x0 - pad, y0: y0 - pad, z0: z0 - pad, x1: x1 + pad, y1: y1 + pad, z1: z1 + pad } };
}

const planMemo = new Map<string, CavePlan>();
/** The plan of a site, memoised per seed (the chunk assembler cuts against it on the main thread). */
export function cavePlanFor(seed: string, site: CaveSite): CavePlan {
  const key = `${seed}:${site.id}`;
  let p = planMemo.get(key);
  if (!p) { p = planCave(seed, site); planMemo.set(key, p); }
  return p;
}

/** Signed distance to the cavity (negative inside the air of the tunnel). */
export function caveDensity(plan: CavePlan, x: number, y: number, z: number): number {
  // ONE floor surface for the whole walk: FLOOR_DROP under the axis of the
  // nearest TUNNEL capsule (measured in XZ, so the floor is a function of the
  // ground plan, not of height). Every capsule — chambers included — is
  // clipped by that floor before the union, so a chamber never undercuts the
  // tunnel it opens from and a widening never steps the floor.
  let floor = -Infinity;
  let floorD = Infinity;
  for (const c of plan.caps) {
    if (c.chamber) continue;
    const dx = c.bx - c.ax, dz = c.bz - c.az;
    const px = x - c.ax, pz = z - c.az;
    const l2 = dx * dx + dz * dz;
    let t = l2 > 0 ? (px * dx + pz * dz) / l2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const ex = px - dx * t, ez = pz - dz * t;
    const d2 = ex * ex + ez * ez;
    if (d2 < floorD) { floorD = d2; floor = c.ay + (c.by - c.ay) * t - FLOOR_DROP; }
  }
  let best = Infinity;
  for (const c of plan.caps) {
    const dx = c.bx - c.ax, dy = c.by - c.ay, dz = c.bz - c.az;
    const px = x - c.ax, py = y - c.ay, pz = z - c.az;
    const l2 = dx * dx + dy * dy + dz * dz;
    let t = l2 > 0 ? (px * dx + py * dy + pz * dz) / l2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const ex = px - dx * t, ey = py - dy * t, ez = pz - dz * t;
    const d = Math.sqrt(ex * ex + ey * ey + ez * ez) - c.r;
    const clipped = Math.max(d, floor - y);
    if (clipped < best) best = clipped;
  }
  return best;
}

/**
 * The full field at a mouth-relative point given the ground height there
 * (`surfRel` = terrain − site.h): negative = air. Inside the outcrop's
 * footprint the rock is ground ∪ outcrop minus the cavity; outside it the
 * cavity alone (the ground is the terrain mesh's own business there).
 */
export function caveFieldAt(plan: CavePlan, surfRel: number, x: number, y: number, z: number): number {
  return Math.min(surfRel + portalDome(plan.site, x, z) - y, caveDensity(plan, x, y, z));
}

/**
 * Should the chunk assembler drop this terrain triangle (mouth-relative
 * vertices, y = the rendered height)? Yes when it stands under the portal
 * outcrop AND any of its vertices, edge midpoints or centroid is inside (or
 * within CAVE_CUT_MARGIN of) the cavity air — the hole the tunnel enters by,
 * never wider than the outcrop that roofs it.
 */
export function caveCutsTriangle(plan: CavePlan, ax: number, ay: number, az: number, bx: number, by: number, bz: number, cx: number, cy: number, cz: number): boolean {
  const mx = (ax + bx + cx) / 3, my = (ay + by + cy) / 3, mz = (az + bz + cz) / 3;
  if (portalDome(plan.site, mx, mz) <= 0) return false;
  // A contact only counts where the outcrop roofs it: a corner that meets the
  // hall's air OUTSIDE the outcrop (rising ground in front of the arch) keeps
  // its ground — the terrain stays a lid there, never a hole into the void.
  const touch = (x: number, y: number, z: number): boolean => caveDensity(plan, x, y, z) < CAVE_CUT_MARGIN && portalDome(plan.site, x, z) > 0;
  return touch(mx, my, mz) || touch(ax, ay, az) || touch(bx, by, bz) || touch(cx, cy, cz)
    || touch((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2) || touch((bx + cx) / 2, (by + cy) / 2, (bz + cz) / 2) || touch((ax + cx) / 2, (ay + cy) / 2, (az + cz) / 2);
}

export interface CaveMesh { positions: Float32Array; normals: Float32Array; indices: Uint32Array; triangles: number }

/** Marching cubes over the plan's bbox; positions mouth-relative. */
export function meshCave(seed: string, plan: CavePlan, voxel = CAVE_VOXEL): CaveMesh {
  const { site } = plan;
  const bbox = { ...plan.bbox };
  const nx = Math.ceil((bbox.x1 - bbox.x0) / voxel) + 1;
  const nz = Math.ceil((bbox.z1 - bbox.z0) / voxel) + 1;
  // Ground + outcrop per column (the outcrop's height range widens the box).
  const surfCache = new Float32Array(nx * nz);
  const domeCache = new Float32Array(nx * nz);
  for (let iz = 0; iz < nz; iz++) {
    for (let ix = 0; ix < nx; ix++) {
      const wx = bbox.x0 + ix * voxel, wz = bbox.z0 + iz * voxel;
      const s = terrainHeight(seed, site.x + wx, site.z + wz) - site.h;
      const d = portalDome(site, wx, wz);
      surfCache[iz * nx + ix] = s;
      domeCache[iz * nx + ix] = d;
      if (d > 0) {
        bbox.y0 = Math.min(bbox.y0, s - voxel * 2);
        bbox.y1 = Math.max(bbox.y1, s + d + voxel * 2);
      }
    }
  }
  const ny = Math.ceil((bbox.y1 - bbox.y0) / voxel) + 1;
  const field = new Float32Array(nx * ny * nz);
  for (let iz = 0; iz < nz; iz++) {
    for (let ix = 0; ix < nx; ix++) {
      const s = surfCache[iz * nx + ix], d = domeCache[iz * nx + ix];
      const x = bbox.x0 + ix * voxel, z = bbox.z0 + iz * voxel;
      for (let iy = 0; iy < ny; iy++) {
        const y = bbox.y0 + iy * voxel;
        // Air = above the ground-and-outcrop OR inside the cavity, everywhere
        // (one convention across the outcrop's rim — a field that treated the
        // outside as rock raised a curtain of triangles along the rim).
        field[(iz * ny + iy) * nx + ix] = Math.min(s + d - y, caveDensity(plan, x, y, z));
      }
    }
  }
  const at = (ix: number, iy: number, iz: number): number => field[(iz * ny + iy) * nx + ix];
  // Ground + outcrop at any (x, z): bilinear over the column caches.
  const colAt = (x: number, z: number): { s: number; d: number } => {
    const fx = (x - bbox.x0) / voxel, fz = (z - bbox.z0) / voxel;
    const ix = Math.min(nx - 2, Math.max(0, Math.floor(fx))), iz = Math.min(nz - 2, Math.max(0, Math.floor(fz)));
    const tx = Math.min(1, Math.max(0, fx - ix)), tz = Math.min(1, Math.max(0, fz - iz));
    const i00 = iz * nx + ix, i10 = i00 + 1, i01 = i00 + nx, i11 = i01 + 1;
    const lerp2 = (arr: Float32Array): number => (arr[i00] * (1 - tx) + arr[i10] * tx) * (1 - tz) + (arr[i01] * (1 - tx) + arr[i11] * tx) * tz;
    return { s: lerp2(surfCache), d: lerp2(domeCache) };
  };
  const positions: number[] = [];
  const indices: number[] = [];
  const vcache = new Map<string, number>();
  const corner = [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1], [0, 1, 0], [1, 1, 0], [1, 1, 1], [0, 1, 1]];
  const edgeV = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]];
  const vals = new Float64Array(8);
  const edgeIdx = new Int32Array(12);
  for (let iz = 0; iz < nz - 1; iz++) {
    for (let iy = 0; iy < ny - 1; iy++) {
      for (let ix = 0; ix < nx - 1; ix++) {
        let cube = 0;
        for (let c = 0; c < 8; c++) {
          vals[c] = at(ix + corner[c][0], iy + corner[c][1], iz + corner[c][2]);
          if (vals[c] < 0) cube |= 1 << c;
        }
        const em = edgeTable[cube];
        if (em === 0) continue;
        for (let e = 0; e < 12; e++) {
          if (!(em & (1 << e))) { edgeIdx[e] = -1; continue; }
          const [a, b] = edgeV[e];
          const ca = corner[a], cb = corner[b];
          const key = `${ix + ca[0]},${iy + ca[1]},${iz + ca[2]}|${ix + cb[0]},${iy + cb[1]},${iz + cb[2]}`;
          let vi = vcache.get(key);
          if (vi === undefined) {
            const va = vals[a], vb = vals[b];
            const t = Math.abs(vb - va) < 1e-9 ? 0.5 : (0 - va) / (vb - va);
            positions.push(
              bbox.x0 + (ix + ca[0] + (cb[0] - ca[0]) * t) * voxel,
              bbox.y0 + (iy + ca[1] + (cb[1] - ca[1]) * t) * voxel,
              bbox.z0 + (iz + ca[2] + (cb[2] - ca[2]) * t) * voxel,
            );
            vi = positions.length / 3 - 1;
            vcache.set(key, vi);
          }
          edgeIdx[e] = vi;
        }
        const base = cube * 16;
        for (let i = 0; triTable[base + i] !== -1; i += 3) {
          const a = edgeIdx[triTable[base + i]], b = edgeIdx[triTable[base + i + 1]], c = edgeIdx[triTable[base + i + 2]];
          if (a < 0 || b < 0 || c < 0) continue;
          const cx = (positions[a * 3] + positions[b * 3] + positions[c * 3]) / 3;
          const cy = (positions[a * 3 + 1] + positions[b * 3 + 1] + positions[c * 3 + 1]) / 3;
          const cz = (positions[a * 3 + 2] + positions[b * 3 + 2] + positions[c * 3 + 2]) / 3;
          // Keep the outcrop's skin and the cavity; drop the bare ground the
          // field emits wherever the cap is absent or thins to nothing (that
          // surface is the terrain mesh's own).
          const { s, d } = colAt(cx, cz);
          if (d < 0.02 && s + d - cy <= caveDensity(plan, cx, cy, cz)) continue;
          // Wound so the FRONT face looks into the air, agreeing with the
          // stored normals: the tables' order faced the rock, and a
          // double-sided material flips the normal on a back face — every
          // wall and the whole outcrop shaded as if lit from inside the rock
          // (the black interiors, 2026-09-10).
          indices.push(a, c, b);
        }
      }
    }
  }
  // Normals: the field's gradient pointing INTO the air (toward negative).
  const fieldAt = (x: number, y: number, z: number): number => {
    const { s, d } = colAt(x, z);
    return Math.min(s + d - y, caveDensity(plan, x, y, z));
  };
  const normals = new Float32Array(positions.length);
  const h = voxel * 0.5;
  for (let v = 0; v < positions.length / 3; v++) {
    const x = positions[v * 3], y = positions[v * 3 + 1], z = positions[v * 3 + 2];
    let gx = fieldAt(x + h, y, z) - fieldAt(x - h, y, z);
    let gy = fieldAt(x, y + h, z) - fieldAt(x, y - h, z);
    let gz = fieldAt(x, y, z + h) - fieldAt(x, y, z - h);
    const l = Math.sqrt(gx * gx + gy * gy + gz * gz) || 1;
    gx /= -l; gy /= -l; gz /= -l;
    normals[v * 3] = gx; normals[v * 3 + 1] = gy; normals[v * 3 + 2] = gz;
  }
  return { positions: new Float32Array(positions), normals, indices: new Uint32Array(indices), triangles: indices.length / 3 };
}
